<script setup>
import { computed } from 'vue';
import { FileText, Loader2 } from 'lucide-vue-next';
import Tooltip2 from '@/components/Tooltip2.vue';

const props = defineProps({
  asset: Object,
});

const emit = defineEmits(['click', 'remove']);

const displayFilename = computed(() => {
  const filename = props.asset.filename || props.asset.meta?.filename || '';
  const maxLength = 20;

  if (filename.length <= maxLength) {
    return filename;
  }

  // Extract extension
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return filename.slice(0, maxLength - 3) + '...';
  }

  const name = filename.slice(0, lastDotIndex);
  const ext = filename.slice(lastDotIndex);
  const availableLength = maxLength - ext.length - 3; // 3 for '...'

  if (availableLength > 0) {
    return name.slice(0, availableLength) + '...' + ext;
  }

  return filename.slice(0, maxLength - 3) + '...';
});

const fullFilename = computed(() => {
  return props.asset.filename || props.asset.meta?.filename || '';
});

function removeAsset(asset) {
  emit('remove', asset);
}
</script>
<template>
  <Tooltip2 :text="fullFilename">
    <div
      class="group relative inline-flex h-fit items-center gap-2 px-3 py-2 rounded-xl bg-[#f2f3f7] transition-all duration-300 shrink-0"
    >
      <div class="flex items-center gap-2 cursor-pointer select-none">
        <!-- Document preview -->
        <div class="relative">
          <div
            class="w-6 h-6 flex items-center justify-center bg-white rounded-lg shadow-sm ring-1 ring-white/50"
          >
            <!-- Show loading spinner when parsing, otherwise show file icon -->
            <Loader2 v-if="!asset.parsed" class="w-3.5 h-3.5 text-blue-500 animate-spin" />
            <FileText v-else class="w-3.5 h-3.5 text-black/60" />
          </div>
          <div
            class="absolute inset-0 rounded-lg bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
          ></div>
        </div>

        <!-- Label -->
        <span class="text-slate-700 text-xs font-medium tracking-tight">
          {{ displayFilename }}
          <span v-if="!asset.parsed" class="text-blue-500 ml-1">{{ $t('attach.parsing') }}</span>
        </span>
      </div>

      <!-- Remove button -->
      <button
        @click="removeAsset(asset)"
        class="absolute -top-1 -right-1 w-5 h-5 bg-slate-800/90 hover:bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 opacity-0 group-hover:opacity-100 hover:scale-110"
        :title="$t('attach.remove')"
      >
        <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2.5"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  </Tooltip2>
</template>
